import { useState, useMemo, useCallback } from 'react';
import { catalogFilterOptions, catalogProducts } from '@/mocks/catalog';

export interface FilterState {
  sections: string[];
  categories: string[];
  brands: string[];
  manufacturers: string[];
  countries: string[];
  forms: string[];
  activeSubstances: string[];
  priceMin: string;
  priceMax: string;
  prescription: string;
  inStock: boolean;
  hasDiscount: boolean;
}

interface FilterGroupProps {
  title: string;
  options: string[];
  selected: string[];
  onToggle: (val: string) => void;
  color: string;
}

const FilterGroup = ({ title, options, selected, onToggle, color }: FilterGroupProps) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  const visible = showAll ? filtered : filtered.slice(0, 5);

  return (
    <div className="border-b border-white/5 pb-4 mb-4">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between mb-3 cursor-pointer group"
      >
        <span className="font-orbitron font-bold text-xs text-white/80 tracking-wider">{title}</span>
        <i
          className={`text-white/30 text-sm transition-transform duration-200 ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}
        />
      </button>

      {expanded && (
        <>
          {/* Search inside filter */}
          <div className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-xs text-white/30" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск..."
              className="w-full bg-white/5 border border-white/8 rounded-lg pl-8 pr-3 py-1.5 font-mono text-xs text-white placeholder-white/20 outline-none focus:border-white/20 transition-all"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
                <i className="ri-close-line text-xs text-white/30 hover:text-white/60" />
              </button>
            )}
          </div>

          <div className="space-y-1.5">
            {visible.map((opt) => {
              const checked = selected.includes(opt);
              return (
                <label key={opt} className="flex items-center gap-2.5 cursor-pointer group/item">
                  <div
                    onClick={() => onToggle(opt)}
                    className="w-4 h-4 rounded flex items-center justify-center shrink-0 transition-all duration-200 cursor-pointer"
                    style={
                      checked
                        ? { background: color, border: `1px solid ${color}`, boxShadow: `0 0 6px ${color}60` }
                        : { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)' }
                    }
                  >
                    {checked && <i className="ri-check-line text-[9px] text-black font-bold" />}
                  </div>
                  <span
                    className="font-mono text-xs leading-tight transition-colors"
                    style={{ color: checked ? color : 'rgba(255,255,255,0.5)' }}
                    onClick={() => onToggle(opt)}
                  >
                    {opt}
                  </span>
                </label>
              );
            })}
          </div>

          {filtered.length > 5 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 font-mono text-xs cursor-pointer transition-colors"
              style={{ color: color }}
            >
              {showAll ? 'Свернуть' : `Ещё ${filtered.length - 5}`}
            </button>
          )}

          {filtered.length === 0 && (
            <p className="font-mono text-xs text-white/20 italic">Ничего не найдено</p>
          )}
        </>
      )}
    </div>
  );
};

// Price Range Slider Component
interface PriceRangeSliderProps {
  min: number;
  max: number;
  valueMin: string;
  valueMax: string;
  onChange: (min: string, max: string) => void;
}

const SLIDER_STYLE = `
  .price-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; background: transparent; outline: none; position: absolute; pointer-events: none; }
  .price-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 18px; height: 18px; border-radius: 50%; cursor: pointer; pointer-events: all; transition: transform 0.15s, box-shadow 0.15s; }
  .price-slider::-moz-range-thumb { width: 18px; height: 18px; border-radius: 50%; border: none; cursor: pointer; pointer-events: all; transition: transform 0.15s, box-shadow 0.15s; }
  .price-slider.min-slider::-webkit-slider-thumb { background: #ff2d78; box-shadow: 0 0 10px rgba(255,45,120,0.7); }
  .price-slider.min-slider::-moz-range-thumb { background: #ff2d78; box-shadow: 0 0 10px rgba(255,45,120,0.7); }
  .price-slider.max-slider::-webkit-slider-thumb { background: #00f5ff; box-shadow: 0 0 10px rgba(0,245,255,0.7); }
  .price-slider.max-slider::-moz-range-thumb { background: #00f5ff; box-shadow: 0 0 10px rgba(0,245,255,0.7); }
  .price-slider.min-slider::-webkit-slider-thumb:hover { transform: scale(1.2); box-shadow: 0 0 16px rgba(255,45,120,0.9); }
  .price-slider.max-slider::-webkit-slider-thumb:hover { transform: scale(1.2); box-shadow: 0 0 16px rgba(0,245,255,0.9); }
`;

const PriceRangeSlider = ({ min, max, valueMin, valueMax, onChange }: PriceRangeSliderProps) => {
  const curMin = valueMin ? Number(valueMin) : min;
  const curMax = valueMax ? Number(valueMax) : max;

  const minPercent = ((curMin - min) / (max - min)) * 100;
  const maxPercent = ((curMax - min) / (max - min)) * 100;

  const handleMinSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.min(Number(e.target.value), curMax - 1);
    onChange(String(val), String(curMax));
  };

  const handleMaxSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.max(Number(e.target.value), curMin + 1);
    onChange(String(curMin), String(val));
  };

  const handleMinInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || Number(val) < curMax) onChange(val, String(curMax));
  };

  const handleMaxInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || Number(val) > curMin) onChange(String(curMin), val);
  };

  return (
    <div className="space-y-4">
      <style>{SLIDER_STYLE}</style>

      {/* Dual range track */}
      <div className="relative h-5 mt-1">
        {/* Base track */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
        {/* Active track */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
            background: 'linear-gradient(90deg, #ff2d78, #00f5ff)',
            boxShadow: '0 0 6px rgba(255,45,120,0.4)',
          }}
        />
        {/* Min range input */}
        <input
          type="range"
          min={min}
          max={max}
          step={10}
          value={curMin}
          onChange={handleMinSlider}
          className="price-slider min-slider"
          style={{ top: '50%', transform: 'translateY(-50%)', zIndex: curMin > max - 10 ? 5 : 3 }}
        />
        {/* Max range input */}
        <input
          type="range"
          min={min}
          max={max}
          step={10}
          value={curMax}
          onChange={handleMaxSlider}
          className="price-slider max-slider"
          style={{ top: '50%', transform: 'translateY(-50%)', zIndex: 4 }}
        />
      </div>

      {/* Current values display */}
      <div className="flex items-center justify-between">
        <div
          className="px-2.5 py-1 rounded-lg font-orbitron font-bold text-xs"
          style={{ background: 'rgba(255,45,120,0.12)', border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}
        >
          {curMin.toLocaleString()} ₽
        </div>
        <div className="h-px flex-1 mx-2" style={{ background: 'rgba(255,255,255,0.1)' }} />
        <div
          className="px-2.5 py-1 rounded-lg font-orbitron font-bold text-xs"
          style={{ background: 'rgba(0,245,255,0.12)', border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff' }}
        >
          {curMax.toLocaleString()} ₽
        </div>
      </div>

      {/* Manual number inputs */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="number"
            placeholder={String(min)}
            value={valueMin}
            onChange={handleMinInput}
            className="w-full rounded-lg px-3 py-2 font-mono text-xs text-white placeholder-white/20 outline-none transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,45,120,0.2)' }}
            onFocus={e => (e.currentTarget.style.borderColor = 'rgba(255,45,120,0.5)')}
            onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,45,120,0.2)')}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10px] text-white/30">от</span>
        </div>
        <span className="text-white/20 font-mono text-xs shrink-0">—</span>
        <div className="relative flex-1">
          <input
            type="number"
            placeholder={String(max)}
            value={valueMax}
            onChange={handleMaxInput}
            className="w-full rounded-lg px-3 py-2 font-mono text-xs text-white placeholder-white/20 outline-none transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,245,255,0.2)' }}
            onFocus={e => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.5)')}
            onBlur={e => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.2)')}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10px] text-white/30">до</span>
        </div>
      </div>

      {/* Quick price presets */}
      <div className="flex flex-wrap gap-1.5">
        {[
          { label: 'до 500₽', min: min, max: 500 },
          { label: '500–1500₽', min: 500, max: 1500 },
          { label: '1500–3000₽', min: 1500, max: 3000 },
          { label: 'от 3000₽', min: 3000, max: max },
        ].map(preset => {
          const isActive = curMin === preset.min && curMax === preset.max;
          return (
            <button
              key={preset.label}
              onClick={() => onChange(String(preset.min), String(preset.max))}
              className="whitespace-nowrap px-2.5 py-1 rounded-full font-mono text-[10px] cursor-pointer transition-all"
              style={isActive
                ? { background: '#ff2d78', color: '#050508', boxShadow: '0 0 8px rgba(255,45,120,0.5)' }
                : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
              }
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {/* Reset price */}
      {(valueMin || valueMax) && (
        <button
          onClick={() => onChange('', '')}
          className="whitespace-nowrap w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg font-mono text-xs cursor-pointer transition-all"
          style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ff2d78'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
        >
          <i className="ri-close-line text-xs" />
          Сбросить цену
        </button>
      )}
    </div>
  );
};

interface Props {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  onReset: () => void;
  activeCount: number;
  onApply?: () => void;
  showApplyButton?: boolean;
}

const CatalogFilters = ({ filters, onChange, onReset, activeCount, onApply, showApplyButton }: Props) => {
  const toggle = useCallback((key: keyof Pick<FilterState, 'sections' | 'categories' | 'brands' | 'manufacturers' | 'countries' | 'forms' | 'activeSubstances'>, val: string) => {
    const arr = filters[key];
    onChange({
      ...filters,
      [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val],
    });
  }, [filters, onChange]);

  // Calculate min/max prices from catalog
  const { minPrice, maxPrice } = useMemo(() => {
    const prices = catalogProducts.map(p => p.price);
    return {
      minPrice: Math.floor(Math.min(...prices) / 10) * 10,
      maxPrice: Math.ceil(Math.max(...prices) / 10) * 10,
    };
  }, []);

  const handlePriceChange = useCallback((min: string, max: string) => {
    onChange({ ...filters, priceMin: min, priceMax: max });
  }, [filters, onChange]);

  return (
    <aside className="w-full lg:w-64 shrink-0">
      <div className="card-cyber rounded-2xl p-5 sticky top-24" style={{ borderColor: 'rgba(255,45,120,0.15)' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <i className="ri-filter-3-line text-sm" style={{ color: '#ff2d78' }} />
            <span className="font-orbitron font-bold text-sm text-white">ФИЛЬТРЫ</span>
            {activeCount > 0 && (
              <span className="w-5 h-5 flex items-center justify-center rounded-full font-mono text-[10px] font-bold" style={{ background: '#ff2d78', color: '#050508' }}>
                {activeCount}
              </span>
            )}
          </div>
          {activeCount > 0 && (
            <button onClick={onReset} className="font-mono text-xs cursor-pointer transition-colors whitespace-nowrap" style={{ color: '#ff2d78' }}>
              Сбросить
            </button>
          )}
        </div>

        {/* Price range with slider */}
        <div className="border-b border-white/5 pb-4 mb-4">
          <p className="font-orbitron font-bold text-xs text-white/80 tracking-wider mb-3">ЦЕНА, ₽</p>
          <PriceRangeSlider
            min={minPrice}
            max={maxPrice}
            valueMin={filters.priceMin}
            valueMax={filters.priceMax}
            onChange={handlePriceChange}
          />
        </div>

        {/* Quick toggles */}
        <div className="border-b border-white/5 pb-4 mb-4 space-y-2.5">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="font-mono text-xs text-white/50">Только в наличии</span>
            <div
              onClick={() => onChange({ ...filters, inStock: !filters.inStock })}
              className="w-9 h-5 rounded-full relative transition-all duration-300 cursor-pointer"
              style={{ background: filters.inStock ? '#39ff14' : 'rgba(255,255,255,0.1)', boxShadow: filters.inStock ? '0 0 8px #39ff1460' : 'none' }}
            >
              <div
                className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300"
                style={{ left: filters.inStock ? '18px' : '2px' }}
              />
            </div>
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="font-mono text-xs text-white/50">Со скидкой</span>
            <div
              onClick={() => onChange({ ...filters, hasDiscount: !filters.hasDiscount })}
              className="w-9 h-5 rounded-full relative transition-all duration-300 cursor-pointer"
              style={{ background: filters.hasDiscount ? '#ff2d78' : 'rgba(255,255,255,0.1)', boxShadow: filters.hasDiscount ? '0 0 8px #ff2d7860' : 'none' }}
            >
              <div
                className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300"
                style={{ left: filters.hasDiscount ? '18px' : '2px' }}
              />
            </div>
          </label>
        </div>

        {/* Prescription */}
        <div className="border-b border-white/5 pb-4 mb-4">
          <p className="font-orbitron font-bold text-xs text-white/80 tracking-wider mb-3">ОТПУСК ПО РЕЦЕПТУ</p>
          <div className="flex gap-2">
            {[{ val: 'all', label: 'Все' }, { val: 'no', label: 'Без рецепта' }, { val: 'yes', label: 'По рецепту' }].map((opt) => (
              <button
                key={opt.val}
                onClick={() => onChange({ ...filters, prescription: opt.val })}
                className="whitespace-nowrap flex-1 py-1.5 rounded-lg font-mono text-[10px] cursor-pointer transition-all"
                style={
                  filters.prescription === opt.val
                    ? { background: '#ff2d78', color: '#050508', boxShadow: '0 0 8px #ff2d7860' }
                    : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.08)' }
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <FilterGroup title="РАЗДЕЛ" options={catalogFilterOptions.sections} selected={filters.sections} onToggle={(v) => toggle('sections', v)} color="#ff2d78" />
        <FilterGroup title="КАТЕГОРИЯ" options={catalogFilterOptions.categories} selected={filters.categories} onToggle={(v) => toggle('categories', v)} color="#ff2d78" />
        <FilterGroup title="ФОРМА ВЫПУСКА" options={catalogFilterOptions.forms} selected={filters.forms} onToggle={(v) => toggle('forms', v)} color="#00f5ff" />
        <FilterGroup title="БРЕНД" options={catalogFilterOptions.brands} selected={filters.brands} onToggle={(v) => toggle('brands', v)} color="#39ff14" />
        <FilterGroup title="ПРОИЗВОДИТЕЛЬ" options={catalogFilterOptions.manufacturers ?? []} selected={filters.manufacturers} onToggle={(v) => toggle('manufacturers', v)} color="#f0ff00" />
        <FilterGroup title="СТРАНА" options={catalogFilterOptions.countries} selected={filters.countries} onToggle={(v) => toggle('countries', v)} color="#ff6b00" />
        <FilterGroup title="ДЕЙСТВУЮЩЕЕ ВЕЩЕСТВО" options={catalogFilterOptions.activeSubstances} selected={filters.activeSubstances} onToggle={(v) => toggle('activeSubstances', v)} color="#00f5ff" />

        {/* Mobile Apply Button */}
        {showApplyButton && onApply && (
          <button
            onClick={onApply}
            className="whitespace-nowrap w-full mt-4 py-3 rounded-xl font-orbitron font-bold text-sm cursor-pointer"
            style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px rgba(255,45,120,0.4)' }}
          >
            ПРИМЕНИТЬ ФИЛЬТРЫ
          </button>
        )}
      </div>
    </aside>
  );
};

export default CatalogFilters;